import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { Expense } from '../types';
import { FlatCategory } from '../types/category';
import { api, DUPLICATE_RECEIPT_TOAST, isDuplicateReceiptError } from '../services/api';
import { getSubcategories, findCategoryByName } from '../services/categoryService';
import { saveManualEdits, getManualEditsForExpense } from '../utils/manualEditTracker';
import { formatMetricaDate, toIsoDateOnly as normalizeIsoDate } from '../utils/dateUtils';
import {
  FX_RATE_OVERRIDE_HINT,
  applyOfficialRate,
  applyOriginalTotal,
  clearForeign,
  currencyByCode,
  currencyChoices,
  formatRate,
  isShekel,
  optionalNumber,
  withShekelOverride,
} from '../utils/foreignCurrency';
import MetricaDateInput from '../components/MetricaDateInput/MetricaDateInput';
import SearchableSelect, { optionsFromNames } from '../components/SearchableSelect/SearchableSelect';
import { FIELD_LABELS, getCriticalMissingFields } from '../utils/fieldLabels';
import { noticeForMissingData } from '../utils/missingDataNotice';
import { roundMoney } from '../utils/formatMoney';
import MoneyInput from '../components/MoneyInput/MoneyInput';
import {
  amountsForNoVat,
  amountsFromNet,
  amountsFromTotal,
  amountsFromVat,
  resolveLoadedAmounts,
} from '../utils/amountUtils';
import { NO_VAT_DISPLAY, withCategoryAmountSync } from '../utils/noVat';
import { isCreditDocumentType } from '../utils/creditNotes';
import { markExpenseReviewed, isExpenseReviewed } from '../utils/reviewedExpenses';
import ReviewStatusFilterDropdown from '../components/ReviewStatusFilterDropdown/ReviewStatusFilterDropdown';
import VendorCombobox from '../components/VendorCombobox/VendorCombobox';
import ProjectTagCombobox from '../components/ProjectTagCombobox/ProjectTagCombobox';
import {
  getReviewStatusDotState,
  amountNeedsReviewFromLevel,
  ReviewStatusDotState,
} from '../utils/reviewStatusDot';
import {
  findNextInTableNavList,
  getTableNavNeighbors,
  getTableNavPosition,
  loadTableNavList,
  removeIdsFromTableNavList,
  saveTableNavList,
} from '../utils/tableNavContext';
import { shouldLoadAnotherExpensePage } from '../utils/expensePages';
import { shouldLoadExpenseImage } from '../utils/expenseImage';
import { ExpenseReviewStatusDot } from '../components/ExpenseReviewStatusDot/ExpenseReviewStatusDot';
import reviewDotStyles from '../components/ExpenseReviewStatusDot/ExpenseReviewStatusDot.module.css';
import deleteTrashIcon from '../assets/icons/delete-trash.png';
import RecurrenceDeleteDialog, { RecurrenceDeleteChoice } from '../components/RecurrenceDeleteDialog/RecurrenceDeleteDialog';
import {
  getExpenseRecurrenceMeta,
  isIsoDateInPast,
  toIsoDateOnly,
  todayIsoDate,
} from '../utils/recurrenceSeries';
import styles from './ExpenseEditorPage.module.css';

// Backend response interface for expense data
interface BackendExpenseData {
  id: string;
  serial_number?: string;
  submitted_by?: string;
  vendor_name?: string;
  invoice_number?: string;
  net_amount?: number;
  creation_date?: string;
  creation_timestamp?: string;
  receipt_date?: string;
  notes?: string;
  category_1?: string;
  category_2?: string;
  category_3?: string;
  image_url?: string;
  s3_image_key?: string;
  [key: string]: any; // Allow for additional fields
}

function backendItemToExpense(backendData: BackendExpenseData): Expense {
  const fetchedExpenseId = String(backendData.id);
  const netAmount = roundMoney(Number(backendData.net_amount) || 0);
  const vatAmount = roundMoney(Number(backendData.vat_amount) || 0);
  const rawTotal = backendData.total_amount;
  const parsedTotal =
    rawTotal === undefined || rawTotal === null || rawTotal === ''
      ? undefined
      : Number(rawTotal);
  const loadedTotal =
    parsedTotal !== undefined && Number.isFinite(parsedTotal)
      ? roundMoney(parsedTotal)
      : undefined;
  const resolvedAmounts = Boolean(backendData.no_vat)
    ? amountsForNoVat(netAmount, vatAmount, loadedTotal)
    : resolveLoadedAmounts(netAmount, vatAmount, loadedTotal);
  const noVat = Boolean(backendData.no_vat);
  const manualEditFlags = getManualEditsForExpense(fetchedExpenseId);

  return {
    id: fetchedExpenseId,
    serialNumber: backendData.serial_number || '',
    submittedBy: backendData.submitted_by || '',
    vendorName: backendData.vendor_name || '',
    documentNumber: backendData.invoice_number || '',
    invoiceNumber: backendData.invoice_number || '',
    amount: resolvedAmounts.amount,
    totalAmount: resolvedAmounts.totalAmount,
    creationDate: backendData.creation_date || '',
    creationTimestamp: backendData.creation_timestamp || '',
    documentDate: normalizeIsoDate(backendData.receipt_date || ''),
    notes: backendData.notes || '',
    category1: backendData.category_1 || '',
    category2: backendData.category_2 || '',
    category3: backendData.category_3 || '',
    projectTag: backendData.project_tag || '',
    imageUrl: backendData.image_url || backendData.s3_image_key || '',
    vatAmount: resolvedAmounts.vatAmount,
    noVat,
    isCredit: isCreditDocumentType(backendData.document_type),
    amountNeedsReview: amountNeedsReviewFromLevel(backendData.confidence_level),
    currency: backendData.currency || 'ILS',
    originalNetAmount: optionalNumber(backendData.original_net_amount),
    originalVatAmount: optionalNumber(backendData.original_vat_amount),
    originalTotalAmount: optionalNumber(backendData.original_total_amount),
    fxRate: optionalNumber(backendData.fx_rate),
    fxRateDate: backendData.fx_rate_date ? String(backendData.fx_rate_date).slice(0, 10) : '',
    fxRateOverridden: Boolean(backendData.fx_rate_overridden),
    originalReceiptId:
      backendData.original_receipt_id === undefined || backendData.original_receipt_id === null
        ? null
        : Number(backendData.original_receipt_id),
    manuallyEdited: Object.keys(manualEditFlags).length > 0 ? manualEditFlags : undefined,
    originalData: {
      ...backendData,
      net_amount: resolvedAmounts.amount,
      vat_amount: resolvedAmounts.vatAmount,
      total_amount: resolvedAmounts.totalAmount ?? null,
    },
  };
}

const expenseHasUnsavedChanges = (current: Expense, original: Expense | null): boolean => {
  if (!original) return false;
  const text = (value: unknown) => String(value ?? '').trim();
  const amount = (value: unknown) => Number(value || 0);
  return (
    text(current.vendorName) !== text(original.vendorName) ||
    amount(current.amount) !== amount(original.amount) ||
    amount(current.vatAmount) !== amount(original.vatAmount) ||
    amount(current.totalAmount) !== amount(original.totalAmount) ||
    text(current.documentDate) !== text(original.documentDate) ||
    text(current.category1) !== text(original.category1) ||
    text(current.category2) !== text(original.category2) ||
    text(current.category3) !== text(original.category3) ||
    text(current.projectTag) !== text(original.projectTag) ||
    text(current.notes) !== text(original.notes) ||
    text(current.documentNumber || current.invoiceNumber) !==
      text(original.documentNumber || original.invoiceNumber) ||
    text(current.currency || 'ILS') !== text(original.currency || 'ILS') ||
    amount(current.originalTotalAmount) !== amount(original.originalTotalAmount) ||
    amount(current.fxRate) !== amount(original.fxRate) ||
    Boolean(current.fxRateOverridden) !== Boolean(original.fxRateOverridden)
  );
};

const ExpenseEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageRetry, setImageRetry] = useState(0);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isLocalPreviewOnly, setIsLocalPreviewOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recurrenceDeleteOpen, setRecurrenceDeleteOpen] = useState(false);
  const [isLoadingExpense, setIsLoadingExpense] = useState(false);
  const { state, dispatch } = useApp();
  const { flatCategories, categoryTree } = state;
  const { showError, showWarning, showConfirmation, setLoading } = useToast();

  // Check if we're creating a new expense or editing an existing one
  const isNewExpense = searchParams.get('mode') === 'new';
  const expenseId = searchParams.get('id');

  // Initialize empty expense for new entries
  const emptyExpense: Expense = {
    id: '',
    serialNumber: '',
    submittedBy: '',
    vendorName: '',
    documentNumber: '',
    amount: 0, // Keep as 0 internally but we'll display as empty
    creationDate: '',
    documentDate: '',
    notes: '',
    category1: '',
    category2: '',
    category3: '',
    recurrenceEnabled: false,
    recurrenceFrequency: 'monthly',
    recurrenceCount: 1,
    isCredit: false,
  };

  // Use selected expense from context or fall back to empty expense
  const [expense, setExpense] = useState<Expense>(
    isNewExpense ? emptyExpense : (state.selectedExpense || emptyExpense)
  );

  // Track original expense data for change detection
  const [originalExpense, setOriginalExpense] = useState<Expense | null>(null);
  const [isReviewed, setIsReviewed] = useState(false);
  const [tableNavIds, setTableNavIds] = useState<string[]>(() => loadTableNavList());
  const fallbackNavStarted = useRef(false);
  const fxRequest = useRef(0);
  const [selectedReviewStatuses, setSelectedReviewStatuses] = useState<
    Set<ReviewStatusDotState>
  >(new Set());

  // Update expense when selectedExpense changes — only if it matches the URL
  useEffect(() => {
    if (isNewExpense || !state.selectedExpense) return;
    if (expenseId && String(state.selectedExpense.id) !== String(expenseId)) return;
    // Load manually edited flags from localStorage
    const manualEditFlags = getManualEditsForExpense(state.selectedExpense.id);
    const invoice =
      state.selectedExpense.documentNumber ||
      state.selectedExpense.invoiceNumber ||
      '';
    const expenseWithManualEdits = {
      ...state.selectedExpense,
      documentNumber: invoice,
      invoiceNumber: invoice,
      manuallyEdited: Object.keys(manualEditFlags).length > 0 ? manualEditFlags : state.selectedExpense.manuallyEdited
    };
    
    setExpense(expenseWithManualEdits);
    setIsReviewed(isExpenseReviewed(state.selectedExpense.id));
    // Store the original data for change detection (deep copy to preserve values)
    const originalCopy = { ...expenseWithManualEdits };
    setOriginalExpense(originalCopy);
  }, [state.selectedExpense, isNewExpense, expenseId]);

  // Load the ticket for the URL id. Neighbors and save-and-next use that id + the
  // table's filtered order. Do not key this effect on selectedExpense — that left
  // the loading overlay on after cancelled in-flight requests.
  useEffect(() => {
    if (isNewExpense || !expenseId) return undefined;

    let cancelled = false;
    setIsLoadingExpense(true);

    const goHomeMissing = () => {
      showWarning('ההוצאה לא נמצאה');
      dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
      navigate('/');
    };

    const fetchExpenseData = async () => {
      try {
        const response = await api.expenses.getById(expenseId);
        if (cancelled) return;
        if (response.success && response.data) {
          const responseData = response.data as any;
          const items = responseData.items || [];

          if (items.length === 0) {
            goHomeMissing();
            return;
          }

          const frontendExpense = backendItemToExpense(items[0] as BackendExpenseData);
          setExpense(frontendExpense);
          setIsReviewed(isExpenseReviewed(frontendExpense.id));
          setOriginalExpense({ ...frontendExpense });
          dispatch({ type: 'SET_SELECTED_EXPENSE', payload: frontendExpense });
        } else {
          console.error('Failed to fetch expense:', response.error);
          showError('שגיאה בטעינת נתוני ההוצאה', {
            action: 'Load expense by ID',
            error: response.error,
            context: { expenseId },
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching expense:', error);
        showError('שגיאה בטעינת נתוני ההוצאה', {
          action: 'Load expense by ID',
          error,
          context: { expenseId },
        });
      } finally {
        if (!cancelled) setIsLoadingExpense(false);
      }
    };

    fetchExpenseData();
    return () => {
      cancelled = true;
      setIsLoadingExpense(false);
    };
  }, [isNewExpense, expenseId, dispatch, navigate, showError, showWarning]);

  // Fallback nav list when opening editor directly (no table snapshot).
  // Publish ids as each page arrives so previous/next works before the full list is in.
  useEffect(() => {
    if (isNewExpense || tableNavIds.length > 0 || fallbackNavStarted.current) return;
    fallbackNavStarted.current = true;

    const publishNavIds = (items: any[]) => {
      const ids = items
        .filter((item: any) => item.status !== 'deleted')
        .sort((a, b) => {
          const aTime = new Date(a.creation_timestamp || 0).getTime();
          const bTime = new Date(b.creation_timestamp || 0).getTime();
          if (bTime !== aTime) return bTime - aTime;
          return String(a.id).localeCompare(String(b.id));
        })
        .map((item: any) => String(item.id));

      if (ids.length > 0) {
        saveTableNavList(ids);
        setTableNavIds(ids);
      }
    };

    const buildFallbackNavList = async () => {
      const pageSize = 100;
      const allItems: any[] = [];
      let page = 1;
      let total = Infinity;

      while (allItems.length < total) {
        const response = await api.expenses.getAll({
          page,
          limit: pageSize,
          sortBy: 'creationTimestamp',
          sortOrder: 'desc',
        });
        if (!response.success || !response.data) break;
        const backendData = response.data as { items: any[]; total?: number };
        const batch = backendData.items || [];
        allItems.push(...batch);
        total = Number(backendData.total ?? allItems.length);
        publishNavIds(allItems);
        if (!shouldLoadAnotherExpensePage(allItems.length, total, batch.length, pageSize)) break;
        page += 1;
      }
    };

    buildFallbackNavList();
  }, [isNewExpense, tableNavIds.length]);

  const activeNavId = String(expenseId || '');
  const tableNavNeighbors = useMemo(
    () => getTableNavNeighbors(tableNavIds, activeNavId),
    [tableNavIds, activeNavId]
  );
  const newerNavExpenseId = tableNavNeighbors.prev;
  const olderNavExpenseId = tableNavNeighbors.next;
  const navPosition = useMemo(
    () => getTableNavPosition(tableNavIds, activeNavId),
    [tableNavIds, activeNavId]
  );
  const navBusy = isLoadingExpense || isSaving || isDeleting;

  const handleNavigateExpense = async (targetId: string) => {
    if (!targetId || navBusy || targetId === activeNavId) return;
    if (expenseHasUnsavedChanges(expense, originalExpense)) {
      const confirmed = await showConfirmation({
        title: 'שינויים שלא נשמרו',
        message: 'יש שינויים שלא נשמרו. לעבור להוצאה אחרת בלי לשמור?',
        confirmText: 'כן, בלי לשמור',
        cancelText: 'ביטול',
        type: 'warning',
      });
      if (!confirmed) return;
    }
    navigate(`/editor?id=${targetId}`);
  };

  type SaveAction = 'stay' | 'next' | 'home';

  const performSave = async (action: SaveAction = 'stay'): Promise<boolean> => {
    setIsSaving(true);
    try {
      const criticalMissing = getCriticalMissingFields(expense);
      if (criticalMissing.length > 0) {
        const labels = criticalMissing.map((field) => field.label).join(', ');
        showWarning(`אנא מלאו את השדות החסרים: ${labels}`);
        return false;
      }

      if (isNewExpense) {
        // Create new expense
        const response = await api.expenses.create(expense);
        if (response.success) {
          console.log('Expense created successfully:', response.data);
          const responseData = response.data as any;
          if (responseData?.id) {
            markExpenseReviewed(String(responseData.id));
          }
          if (action === 'home') {
            dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
            navigate('/');
            return true;
          }
          if (responseData?.id) {
            navigate(`/editor?id=${responseData.id}`, { replace: true });
          }
          return true;
        }

        if (isDuplicateReceiptError(response.error)) {
          showWarning(DUPLICATE_RECEIPT_TOAST);
          return false;
        }

        const missingNotice = noticeForMissingData(response.error);
        if (missingNotice) {
          showWarning(missingNotice);
          return false;
        }

        showError(`שגיאה ביצירת ההוצאה: ${response.error}`, {
          action: 'Create expense',
          error: response.error,
        });
        return false;
      }

      // Update existing expense - include original data for change detection
      const expenseWithOriginal = {
        ...expense,
        originalData: originalExpense?.originalData
      };
      const response = await api.expenses.update(expense.id, expenseWithOriginal);
      if (!response.success) {
        const missingNotice = noticeForMissingData(response.error);
        if (missingNotice) {
          showWarning(missingNotice);
          return false;
        }
        showError(`שגיאה בעדכון ההוצאה: ${response.error}`, {
          action: 'Update expense',
          error: response.error,
          context: { expenseId: expense.id },
        });
        return false;
      }

      const responseData = response.data as any;
      markExpenseReviewed(expense.id);
      setIsReviewed(true);

      if (responseData.action !== 'no_changes') {
        const manuallyEdited: any = { ...expense.manuallyEdited };
        
        if (originalExpense) {
          if (expense.vendorName !== originalExpense.vendorName) {
            manuallyEdited.vendorName = true;
          }
          if (expense.category1 !== originalExpense.category1) {
            manuallyEdited.category1 = true;
          }
          if (expense.category2 !== originalExpense.category2) {
            manuallyEdited.category2 = true;
          }
          if (expense.category3 !== originalExpense.category3) {
            manuallyEdited.category3 = true;
          }
          if (expense.notes !== originalExpense.notes) {
            manuallyEdited.notes = true;
          }
          if (expense.documentDate !== originalExpense.documentDate) {
            manuallyEdited.documentDate = true;
          }
          if ((expense.projectTag || '') !== (originalExpense.projectTag || '')) {
            manuallyEdited.projectTag = true;
          }
        }
        
        saveManualEdits(expense.id, manuallyEdited);
        
        const syncedOriginalData = {
          ...(originalExpense?.originalData || expense.originalData || {}),
          vendor_name: expense.vendorName,
          category_1: expense.category1,
          category_2: expense.category2,
          category_3: expense.category3,
          notes: expense.notes,
          receipt_date: expense.documentDate,
          project_tag: expense.projectTag || '',
          invoice_number: expense.documentNumber || expense.invoiceNumber || '',
          net_amount: expense.amount,
          vat_amount: expense.vatAmount,
        };

        const updatedExpense = { 
          ...expense, 
          ...(responseData.updated_expense || {}),
          projectTag: expense.projectTag || '',
          manuallyEdited: manuallyEdited,
          originalData: syncedOriginalData,
        };
        
        dispatch({ type: 'UPDATE_EXPENSE', payload: updatedExpense });
        dispatch({ type: 'SET_SELECTED_EXPENSE', payload: updatedExpense });
        setExpense(updatedExpense);
        setOriginalExpense({ ...updatedExpense });
      }

      sessionStorage.setItem('expenses-reload-after-edit', '1');

      if (action === 'next' && olderNavExpenseId) {
        navigate(`/editor?id=${olderNavExpenseId}`);
      } else if (action === 'home') {
        dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
        navigate('/');
      }

      return true;
    } catch (error) {
      console.error('Error saving expense:', error);
      showError('שגיאה בשמירת ההוצאה', {
        action: 'Save expense',
        error,
        context: { expenseId: expense.id, isNewExpense },
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => performSave('stay');
  const handleSaveAndNext = () => performSave('next');
  const handleSaveAndReturnHome = () => performSave('home');

  useEffect(() => {
    setImagePreview(null);
    setImageError(null);
    setIsLocalPreviewOnly(false);
  }, [expenseId]);

  useEffect(() => {
    const active = isSaving || isDeleting || isLoadingExpense;
    setLoading(active, 'expenseEditor');
    return () => setLoading(false, 'expenseEditor');
  }, [isSaving, isDeleting, isLoadingExpense, setLoading]);

  // Initialize category dropdowns when editing an existing expense
  useEffect(() => {
    if (!isNewExpense && flatCategories && expense.category1) {
      // Find the selected category1 and populate category2 options
      const category1 = findCategoryByName(flatCategories.categories1, expense.category1);
      if (category1) {
        const subcats2 = getSubcategories(flatCategories.allCategories, category1.id, 2);
        setAvailableCategories2(subcats2);
        
        // If category2 exists, populate category3 options
        if (expense.category2) {
          const category2 = findCategoryByName(subcats2, expense.category2);
          if (category2) {
            const subcats3 = getSubcategories(flatCategories.allCategories, category2.id, 3);
            setAvailableCategories3(subcats3);
          }
        }
      }
    }
  }, [flatCategories, isNewExpense, expense.category1, expense.category2]);

  // Fetch the photo only after the form shows the receipt in the address.
  // Otherwise clearing the preview reloads the previous receipt's picture.
  useEffect(() => {
    const imageKey = expense.imageUrl || '';
    if (!shouldLoadExpenseImage({
      isNewExpense,
      urlExpenseId: expenseId,
      loadedExpenseId: expense.id,
      imageKey,
      isLocalPreview: isLocalPreviewOnly,
    })) {
      return undefined;
    }

    let cancelled = false;
    setIsLoadingImage(true);
    setImageError(null);

    const loadImage = async () => {
      try {
        const response = await api.image.getSignedUrl(imageKey);
        if (cancelled) return;
        if (response.success && response.data?.url) {
          setIsLocalPreviewOnly(false);
          setImagePreview(response.data.url);
        } else {
          setImageError('לא ניתן לטעון את התמונה');
          showError('שגיאה בטעינת תמונת החשבונית', {
            action: 'Load expense image',
            error: response.error,
            context: { imageKey },
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch signed URL:', error);
        setImageError('שגיאה בטעינת התמונה');
        showError('שגיאה בטעינת תמונת החשבונית', {
          action: 'Load expense image',
          error,
          context: { imageKey },
        });
      } finally {
        if (!cancelled) setIsLoadingImage(false);
      }
    };

    loadImage();
    return () => {
      cancelled = true;
      setIsLoadingImage(false);
    };
  }, [expense.id, expense.imageUrl, expenseId, imageRetry, isLocalPreviewOnly, isNewExpense, showError]);

  // Required fields for new expenses
  const requiredFields: (keyof Expense)[] = [
    'vendorName',
    'amount',
    'documentDate',
    'category1'
  ];

  // State for dropdown data
  const [availableCategories2, setAvailableCategories2] = useState<FlatCategory[]>([]);
  const [availableCategories3, setAvailableCategories3] = useState<FlatCategory[]>([]);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isDraggingPopup, setIsDraggingPopup] = useState(false);
  const [popupDragStart, setPopupDragStart] = useState({ x: 0, y: 0 });
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [imageDragStart, setImageDragStart] = useState({ x: 0, y: 0 });
  const [imageDropActive, setImageDropActive] = useState(false);

  useEffect(() => {
    if (flatCategories && expense.category1) {
      const category1 = findCategoryByName(flatCategories.categories1, expense.category1);
      if (category1) {
        setAvailableCategories2(getSubcategories(flatCategories.allCategories, category1.id, 2));
      } else {
        setAvailableCategories2([]);
      }
    } else {
      setAvailableCategories2([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense.category1, flatCategories]);

  useEffect(() => {
    if (flatCategories && expense.category1 && expense.category2) {
      const category2 = findCategoryByName(availableCategories2, expense.category2);
      if (category2) {
        setAvailableCategories3(getSubcategories(flatCategories.allCategories, category2.id, 3));
      } else {
        setAvailableCategories3([]);
      }
    } else {
      setAvailableCategories3([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense.category2, availableCategories2, flatCategories, expense.category1]);

  // Handle escape key for image popup
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showImagePopup) {
        setShowImagePopup(false);
      }
    };

    if (showImagePopup) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showImagePopup]);

  // Reset view and center popup when opening
  useEffect(() => {
    if (showImagePopup) {
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
      const popupWidth = 520;
      const popupHeight = 560;
      setPopupPosition({
        x: Math.max(16, (window.innerWidth - popupWidth) / 2),
        y: Math.max(16, (window.innerHeight - popupHeight) / 2),
      });
    }
  }, [showImagePopup]);

  // Drag the floating popup by its header
  useEffect(() => {
    if (!isDraggingPopup) return undefined;

    const onMove = (event: MouseEvent) => {
      setPopupPosition({
        x: event.clientX - popupDragStart.x,
        y: event.clientY - popupDragStart.y,
      });
    };

    const onUp = () => setIsDraggingPopup(false);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingPopup, popupDragStart]);

  const handlePopupHeaderMouseDown = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    setIsDraggingPopup(true);
    setPopupDragStart({
      x: event.clientX - popupPosition.x,
      y: event.clientY - popupPosition.y,
    });
  };

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const delta = event.deltaY > 0 ? 0.92 : 1.08;
    const newZoom = Math.max(0.5, Math.min(4, imageZoom * delta));

    setImageZoom(newZoom);
    if (newZoom <= 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleImageMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (imageZoom <= 1) return;
    setIsDraggingImage(true);
    setImageDragStart({
      x: event.clientX - imagePosition.x,
      y: event.clientY - imagePosition.y,
    });
  };

  const handleImageMouseMove = (event: React.MouseEvent) => {
    if (!isDraggingImage || imageZoom <= 1) return;
    setImagePosition({
      x: event.clientX - imageDragStart.x,
      y: event.clientY - imageDragStart.y,
    });
  };

  const handleImageMouseUp = () => {
    setIsDraggingImage(false);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    event.stopPropagation();
    if (event.touches.length === 1 && imageZoom > 1) {
      setIsDraggingImage(true);
      setImageDragStart({
        x: event.touches[0].clientX - imagePosition.x,
        y: event.touches[0].clientY - imagePosition.y,
      });
    }
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (event.touches.length === 1 && isDraggingImage && imageZoom > 1) {
      setImagePosition({
        x: event.touches[0].clientX - imageDragStart.x,
        y: event.touches[0].clientY - imageDragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDraggingImage(false);
  };

  const closeImagePopup = () => {
    setShowImagePopup(false);
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };
  
  // Format VAT amount for display (for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatVAT = (vatAmount: number | undefined): string => {
    if (vatAmount === undefined || vatAmount === null || vatAmount === 0) {
      return '';
    }
    return roundMoney(vatAmount).toFixed(1);
  };

  const isFieldRequired = (field: keyof Expense) => {
    return isNewExpense && requiredFields.includes(field);
  };

  const isFieldValid = (field: keyof Expense) => {
    if (!isNewExpense) return true;
    const value = expense[field];
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      // Allow empty (0) during typing, but validate on submit
      return value > 0;
    }
    return true;
  };

  const handleInputChange = (field: keyof Expense, value: string | number) => {
    setExpense(prev => {
      if (field === 'amount') {
        const net = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(net) || net === 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = prev.noVat ? amountsForNoVat(net, 0, net) : amountsFromNet(net);
        return withShekelOverride(prev, next);
      }

      if (field === 'vatAmount') {
        if (prev.noVat) return prev;
        const vat = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(vat) || vat === 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = amountsFromVat(vat);
        return withShekelOverride(prev, next);
      }

      if (field === 'totalAmount') {
        const total = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(total) || total === 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = prev.noVat ? amountsForNoVat(0, 0, total) : amountsFromTotal(total);
        return withShekelOverride(prev, next);
      }

      if (field === 'category1') {
        return withCategoryAmountSync(
          prev,
          { category1: String(value), category2: '', category3: '' },
          categoryTree
        );
      }

      if (field === 'category2') {
        return withCategoryAmountSync(
          prev,
          { category1: prev.category1, category2: String(value), category3: '' },
          categoryTree
        );
      }

      if (field === 'category3') {
        return withCategoryAmountSync(
          prev,
          { category1: prev.category1, category2: prev.category2, category3: String(value) },
          categoryTree
        );
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const loadOfficialRate = async (currency: string, documentDate: string) => {
    if (isShekel(currency)) {
      setExpense((prev) => clearForeign({ ...prev, documentDate: documentDate || prev.documentDate }));
      return;
    }
    if (!documentDate) {
      setExpense((prev) => ({
        ...prev,
        currency,
        fxRate: undefined,
        fxRateDate: '',
        fxRateOverridden: false,
      }));
      showWarning('חסר תאריך חשבונית לחישוב השער');
      return;
    }
    const seq = ++fxRequest.current;
    const response = await api.expenses.fxRate(currency, documentDate);
    if (seq !== fxRequest.current) return;
    const rate = Number(response.data?.ils_per_unit);
    if (!response.success || !Number.isFinite(rate) || rate <= 0) {
      setExpense((prev) => ({
        ...prev,
        currency,
        fxRate: undefined,
        fxRateDate: '',
        fxRateOverridden: false,
      }));
      showWarning('לא נמצא שער למטבע בתאריך זה');
      return;
    }
    const rateDate = response.data?.rate_date || documentDate;
    setExpense((prev) => applyOfficialRate(prev, currency, rate, String(rateDate).slice(0, 10)));
  };

  const applyImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showWarning('יש לבחור קובץ תמונה בלבד');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target?.result as string;
      setImageError(null);
      setIsLocalPreviewOnly(true);
      setImagePreview(base64Image);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      applyImageFile(file);
    }
    // Allow selecting the same file again later
    event.target.value = '';
  };

  const handleImageDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setImageDropActive(true);
    } else if (e.type === 'dragleave') {
      setImageDropActive(false);
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDropActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      applyImageFile(file);
    }
  };

  const findNextExpenseAfterDelete = (deletedIds: Set<string>): string | null =>
    findNextInTableNavList(tableNavIds, expense.id || expenseId || '', deletedIds);

  const askDeleteDestination = async (
    title: string,
    message: string
  ): Promise<'next' | 'home' | null> => {
    const result = await showConfirmation({
      title,
      message,
      confirmText: 'מחק',
      secondaryConfirmText: 'מחק וחזור לרשימה',
      cancelText: 'ביטול',
      type: 'danger',
    });
    if (result === 'confirm') return 'next';
    if (result === 'secondary') return 'home';
    return null;
  };

  const executeDelete = async (
    mode: 'single' | 'from_date' | 'series' = 'single',
    fromDate?: string,
    destination: 'next' | 'home' = 'home'
  ) => {
    setIsDeleting(true);
    try {
      const result = await api.expenses.delete(expense.id, { mode, fromDate });
      if (result.success) {
        const responseData = result.data as { ids?: Array<string | number> } | undefined;
        const deletedIds = new Set(
          (responseData?.ids?.length
            ? responseData.ids
            : [expense.id]
          ).map((id) => String(id))
        );

        deletedIds.forEach((id) => {
          dispatch({ type: 'DELETE_EXPENSE', payload: id });
        });
        const updatedNavIds = removeIdsFromTableNavList(deletedIds);
        setTableNavIds(updatedNavIds);

        if (destination === 'next') {
          const nextId = findNextExpenseAfterDelete(deletedIds);
          if (nextId) {
            dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
            navigate(`/editor?id=${nextId}`);
            return;
          }
        }

        dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
        sessionStorage.setItem('expenses-reload-after-edit', '1');
        navigate('/');
      } else {
        showError('שגיאה במחיקת ההוצאה: ' + (result.error || 'שגיאה לא ידועה'), {
          action: 'Delete expense',
          error: result.error,
          context: { expenseId: expense.id, mode },
        });
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      showError('שגיאה במחיקת ההוצאה', {
        action: 'Delete expense',
        error,
        context: { expenseId: expense.id, mode },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRecurrenceDeleteChoice = async (choice: RecurrenceDeleteChoice) => {
    setRecurrenceDeleteOpen(false);
    if (choice === 'single') {
      const destination = await askDeleteDestination(
        'מחיקת הוצאה',
        'האם אתה בטוח שברצונך למחוק את ההוצאה הזו? פעולה זו לא ניתנת לביטול.'
      );
      if (!destination) return;
      await executeDelete('single', undefined, destination);
      return;
    }
    if (choice === 'forward') {
      if (isIsoDateInPast(expense.documentDate)) {
        const confirmed = await showConfirmation({
          title: 'הוצאה מהעבר',
          message: `הוצאה זו היא בעבר (\u2066${formatMetricaDate(expense.documentDate)}\u2069). למחוק מהתאריך הזה והלאה בסדרה?`,
          confirmText: 'כן',
          cancelText: 'לא',
          type: 'warning',
        });
        if (!confirmed) return;
        const destination = await askDeleteDestination(
          'מחיקת הוצאות עתידיות',
          'למחוק מהתאריך הזה והלאה בסדרה? פעולה זו לא ניתנת לביטול.'
        );
        if (!destination) return;
        await executeDelete('from_date', toIsoDateOnly(expense.documentDate), destination);
        return;
      }
      const destination = await askDeleteDestination(
        'מחיקת הוצאות עתידיות',
        'למחוק את כל ההוצאות העתידיות מהיום והלאה? פעולה זו לא ניתנת לביטול.'
      );
      if (!destination) return;
      await executeDelete('from_date', todayIsoDate(), destination);
      return;
    }

    const destination = await askDeleteDestination(
      'מחיקת כל הסדרה',
      'פעולה זו תמחק את כל ההוצאות בסדרה, כולל אלה שכבר עברו. לא ניתן לבטל.'
    );
    if (!destination) return;
    await executeDelete('series', undefined, destination);
  };

  const handleDelete = async () => {
    if (isNewExpense) {
      // For new expenses, just cancel
      navigate('/');
      return;
    }

    if (getExpenseRecurrenceMeta(expense)) {
      setRecurrenceDeleteOpen(true);
      return;
    }

    const destination = await askDeleteDestination(
      'מחיקת הוצאה',
      'האם אתה בטוח שברצונך למחוק את ההוצאה הזו? פעולה זו לא ניתנת לביטול.'
    );
    if (!destination) return;
    await executeDelete('single', undefined, destination);
  };

  const handleCancel = () => {
    // Clear selected expense to ensure clean state when returning
    dispatch({ type: 'SET_SELECTED_EXPENSE', payload: null });
    navigate('/');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitleRow}>
          <h1>{isNewExpense ? 'הוספת הוצאה חדשה' : 'עריכת הוצאה'}</h1>
          {!isNewExpense && (
            <ExpenseReviewStatusDot
              expense={expense}
              reviewed={isReviewed}
              variant="editor"
              className={reviewDotStyles.headerStatusDot}
            />
          )}
          {!isNewExpense && (
            <div className={styles.expenseNav}>
              <span className={styles.expenseNavPosition} dir="ltr">
                (ID : {expense.id || expenseId})
                {navPosition.current > 0 && navPosition.total > 0
                  ? ` ${navPosition.current}/${navPosition.total}`
                  : ''}
              </span>
              <div className={styles.expenseNavArrows}>
              <button
                type="button"
                className={styles.expenseNavButton}
                onClick={() => newerNavExpenseId && handleNavigateExpense(newerNavExpenseId)}
                disabled={!newerNavExpenseId || navBusy}
                aria-label="שורה קודמת ברשימה"
                title="שורה קודמת"
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.expenseNavButton}
                onClick={() => olderNavExpenseId && handleNavigateExpense(olderNavExpenseId)}
                disabled={!olderNavExpenseId || navBusy}
                aria-label="שורה הבאה ברשימה"
                title="שורה הבאה"
              >
                ›
              </button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.headerActions}>
          <button className={styles.backButton} onClick={handleCancel}>
            ← חזור לרשימה
          </button>
          {!isNewExpense && (
            <ReviewStatusFilterDropdown
              selected={selectedReviewStatuses}
              onChange={setSelectedReviewStatuses}
            />
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.formSection}>
          <div className={styles.editLayout}>
            <div className={styles.editTopGrid}>
              {/* Right block: vendor + project tag */}
              <div className={styles.editColumn}>
                <div className={`${styles.formFieldInline} ${!expense.vendorName?.trim() ? styles.fieldMissing : ''}`}>
                  <label>
                    {FIELD_LABELS.vendorName}
                    {isFieldRequired('vendorName') && <span className={styles.required}>*</span>}
                  </label>
                  <div className={styles.fieldControl}>
                    <VendorCombobox
                      value={expense.vendorName || ''}
                      onChange={(vendor) => handleInputChange('vendorName', vendor)}
                      invalid={isNewExpense && !isFieldValid('vendorName')}
                    />
                  </div>
                </div>

                <div className={styles.formFieldInline}>
                  <label>{FIELD_LABELS.projectTag}</label>
                  <div className={styles.fieldControl}>
                    <ProjectTagCombobox
                      value={expense.projectTag || ''}
                      onChange={(projectTag) => handleInputChange('projectTag', projectTag)}
                    />
                  </div>
                </div>

                {isNewExpense && (
                  <div className={`${styles.formFieldInline} ${styles.recurrenceFieldRow}`}>
                    <label className={styles.creditCheckboxOnly}>
                      <input
                        type="checkbox"
                        checked={Boolean(expense.isCredit)}
                        onChange={(event) =>
                          setExpense((prev) => ({ ...prev, isCredit: event.target.checked }))
                        }
                        aria-label={FIELD_LABELS.credit}
                      />
                      <span className={expense.isCredit ? styles.creditCheckboxText : undefined}>
                        {FIELD_LABELS.credit}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Middle block: amounts */}
              <div className={`${styles.editColumn} ${styles.editColumnAmounts}`}>
                <div className={`${styles.formFieldInline} ${!expense.amount ? styles.fieldMissing : ''}`}>
                  <label>
                    {FIELD_LABELS.amountShort}
                    {isFieldRequired('amount') && <span className={styles.required}>*</span>}
                  </label>
                  <div className={styles.fieldControl}>
                    <MoneyInput
                      value={expense.amount}
                      onChange={(next) => handleInputChange('amount', next)}
                      emptyIfZero
                      className={isNewExpense && !isFieldValid('amount') ? styles.invalidField : ''}
                    />
                  </div>
                </div>

                <div className={styles.formFieldInline}>
                  <label>{FIELD_LABELS.vatAmountShort}</label>
                  <div className={styles.fieldControl}>
                    {expense.noVat ? (
                      <input
                        type="text"
                        readOnly
                        value={NO_VAT_DISPLAY}
                        dir="ltr"
                        className={`${styles.readOnlyField} ${styles.ltrField}`}
                        title="ללא מע״מ"
                      />
                    ) : (
                      <MoneyInput
                        value={expense.vatAmount || 0}
                        onChange={(next) => handleInputChange('vatAmount', next)}
                        emptyIfZero
                      />
                    )}
                  </div>
                </div>

                <div className={styles.formFieldInline}>
                  <label>{FIELD_LABELS.totalAmountShort}</label>
                  <div className={styles.fieldControl}>
                    <MoneyInput
                      value={Number(expense.totalAmount) || 0}
                      onChange={(next) => handleInputChange('totalAmount', next)}
                      emptyIfZero
                      title="סכום כולל (כולל מע״מ)"
                    />
                  </div>
                </div>
              </div>

              {/* Left block: dates — upload date above, ticket date below */}
              <div className={`${styles.editColumn} ${styles.editColumnDates}`}>
                <div className={styles.formFieldInline}>
                  <label>{FIELD_LABELS.creationTimestamp}</label>
                  <div className={styles.fieldControl}>
                    <input
                      type="text"
                      value={expense.creationTimestamp ? formatMetricaDate(expense.creationTimestamp) : 'לא זמין'}
                      readOnly
                      className={`${styles.readOnlyField} ${styles.ltrField} ${styles.uploadDateField}`}
                    />
                  </div>
                </div>

                <div className={`${styles.formFieldInline} ${!expense.documentDate ? styles.fieldMissing : ''}`}>
                  <label>
                    {FIELD_LABELS.documentDate}
                    {isFieldRequired('documentDate') && <span className={styles.required}>*</span>}
                  </label>
                  <div className={styles.fieldControl}>
                    <MetricaDateInput
                      value={expense.documentDate || ''}
                      onChange={(isoDate) => {
                        handleInputChange('documentDate', isoDate);
                        if (!isShekel(expense.currency) && !expense.fxRateOverridden) {
                          void loadOfficialRate(expense.currency || 'ILS', isoDate);
                        }
                      }}
                      ariaLabel={FIELD_LABELS.documentDate}
                      invalid={isNewExpense && !isFieldValid('documentDate')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.editCategoriesRow}>
              <div className={`${styles.formFieldInline} ${!expense.category1 ? styles.fieldMissing : ''}`}>
                <label>
                  {FIELD_LABELS.category1Short}
                  {isFieldRequired('category1') && <span className={styles.required}>*</span>}
                </label>
                <div className={styles.fieldControl}>
                  <SearchableSelect
                    value={expense.category1}
                    onChange={(next) => handleInputChange('category1', next)}
                    options={optionsFromNames(flatCategories?.categories1 || [], expense.category1)}
                    ariaLabel={FIELD_LABELS.category1Short}
                    invalid={isNewExpense && !isFieldValid('category1')}
                    placeholder="בחר קטגוריה"
                  />
                </div>
              </div>
              <div className={styles.formFieldInline}>
                <label>{FIELD_LABELS.category2Short}</label>
                <div className={styles.fieldControl}>
                  <SearchableSelect
                    value={expense.category2}
                    onChange={(next) => handleInputChange('category2', next)}
                    options={optionsFromNames(availableCategories2, expense.category2)}
                    ariaLabel={FIELD_LABELS.category2Short}
                    disabled={!expense.category1}
                    emptyOption={{ value: '', label: 'n/a' }}
                  />
                </div>
              </div>
              <div className={styles.formFieldInline}>
                <label>{FIELD_LABELS.category3Short}</label>
                <div className={styles.fieldControl}>
                  <SearchableSelect
                    value={expense.category3}
                    onChange={(next) => handleInputChange('category3', next)}
                    options={optionsFromNames(availableCategories3, expense.category3)}
                    ariaLabel={FIELD_LABELS.category3Short}
                    disabled={!expense.category2}
                    emptyOption={{ value: '', label: 'n/a' }}
                  />
                </div>
              </div>
            </div>

            <div className={styles.editNotesRow}>
              <div className={styles.notesBlock}>
              <label className={styles.notesLabel}>{FIELD_LABELS.notes}</label>
              <div className={styles.notesField}>
                <textarea
                  dir="auto"
                  value={expense.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={5}
                />
              </div>
              </div>

              <table className={styles.notesSideFields}>
                <tbody>
                  <tr className={!expense.documentNumber ? styles.fieldMissing : undefined}>
                    <th scope="row">{FIELD_LABELS.invoiceNumber}</th>
                    <td>
                      <input
                        type="text"
                        value={expense.documentNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExpense((prev) => ({
                            ...prev,
                            documentNumber: value,
                            invoiceNumber: value,
                          }));
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{FIELD_LABELS.submittedBy}</th>
                    <td>
                      <input
                        type="text"
                        value={expense.submittedBy || ''}
                        readOnly
                        className={styles.readOnlyField}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{FIELD_LABELS.currency}</th>
                    <td>
                      <select
                        className={styles.currencySelect}
                        dir="ltr"
                        value={expense.currency || 'ILS'}
                        aria-label={FIELD_LABELS.currency}
                        onChange={(event) => {
                          void loadOfficialRate(event.target.value, expense.documentDate || '');
                        }}
                      >
                        {currencyChoices(expense.currency).map((option) => (
                          <option key={option.code} value={option.code}>
                            {`\u200F${option.name}`}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  {!isShekel(expense.currency) && (
                    <>
                      <tr>
                        <th scope="row">
                          <span className={styles.fxNameWithUnit}>
                            <span>{FIELD_LABELS.originalAmount}</span>
                            <span className={styles.fxUnitMark}>{currencyByCode(expense.currency).symbol}</span>
                          </span>
                        </th>
                        <td>
                          <MoneyInput
                            value={expense.originalTotalAmount || 0}
                            onChange={(next) => setExpense((prev) => applyOriginalTotal(prev, next))}
                            emptyIfZero
                          />
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <span className={styles.fxNameWithUnit}>
                            <span>{FIELD_LABELS.fxRate}</span>
                            {!expense.fxRateOverridden && expense.fxRateDate ? (
                              <span className={styles.fxRateDate}>({formatMetricaDate(expense.fxRateDate)})</span>
                            ) : null}
                          </span>
                        </th>
                        <td>
                          <input
                            type="text"
                            readOnly
                            dir="ltr"
                            className={`${styles.readOnlyField} ${styles.ltrField}`}
                            value={formatRate(expense.fxRate)}
                            title={expense.fxRateOverridden ? FX_RATE_OVERRIDE_HINT : undefined}
                          />
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button className={styles.saveButton} onClick={handleSave} disabled={isSaving || isLoadingExpense}>
              {isSaving ? 'שומר...' : (isNewExpense ? 'הוסף הוצאה' : 'שמור')}
            </button>
            {!isNewExpense && (
              <button
                className={styles.saveButton}
                onClick={handleSaveAndNext}
                disabled={isSaving || isLoadingExpense || !olderNavExpenseId}
              >
                {isSaving ? 'שומר...' : 'שמור והוצאה הבאה'}
              </button>
            )}
            {!isNewExpense && (
              <button 
                className={styles.deleteButton} 
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
                aria-label="מחק הוצאה"
                title="מחק הוצאה"
              >
                {isDeleting ? (
                  <span className={styles.deleteButtonSpinner} aria-hidden />
                ) : (
                  <img
                    src={deleteTrashIcon}
                    alt=""
                    className={styles.deleteIcon}
                    aria-hidden
                  />
                )}
              </button>
            )}
          </div>
        </div>

        {!isNewExpense && (
          <div className={styles.imageSection}>
          <div className={styles.imageSectionBody}>
          <div
            className={`${styles.imageUpload} ${imageDropActive ? styles.imageUploadDropActive : ''}`.trim()}
            onDragEnter={!imagePreview && !isLoadingImage ? handleImageDrag : undefined}
            onDragLeave={!imagePreview && !isLoadingImage ? handleImageDrag : undefined}
            onDragOver={!imagePreview && !isLoadingImage ? handleImageDrag : undefined}
            onDrop={!imagePreview && !isLoadingImage ? handleImageDrop : undefined}
          >
            {isLoadingImage ? (
              <div className={styles.uploadPlaceholder}>
                <div className={styles.uploadIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                  </svg>
                </div>
                <p>טוען תמונה...</p>
              </div>
            ) : imageError ? (
              <div className={styles.uploadPlaceholder}>
                <div className={styles.uploadIcon} style={{ color: '#dc3545' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                  </svg>
                </div>
                <p style={{ color: '#dc3545' }}>{imageError}</p>
                <button 
                  className={styles.uploadButton}
                  onClick={() => {
                    setImageError(null);
                    setIsLocalPreviewOnly(false);
                    setImagePreview(null);
                    setImageRetry((retry) => retry + 1);
                  }}
                >
                  נסה שוב
                </button>
              </div>
            ) : imagePreview ? (
              <div className={styles.imagePreview}>
                <img 
                  src={imagePreview} 
                  alt="Invoice preview" 
                  onClick={() => setShowImagePopup(true)}
                  className={styles.clickableImage}
                />
                {isLocalPreviewOnly && (
                  <p className={styles.previewOnlyNotice}>
                    שים לב: התמונה לתצוגה מקדימה בלבד. היא אינה נשמרת כמו קובץ שהועלה בוואטסאפ.
                  </p>
                )}
              </div>
            ) : (
              <label htmlFor="imageUpload" className={styles.uploadPlaceholder}>
                <div className={styles.uploadIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                  </svg>
                </div>
                <p className={styles.emptyImageMessage}>ללא תמונה. לחץ או גרור להוספת תמונה</p>
                <p className={styles.previewOnlyNotice}>
                  שים לב: קובץ שתעלה כאן ישמש לתצוגה מקדימה בלבד, ואינו נשמר כמו קובץ שהועלה בוואטסאפ.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className={styles.fileInput}
                  id="imageUpload"
                />
              </label>
            )}
          </div>
          </div>
          <div className={styles.imageSectionActions}>
            <button
              className={styles.saveButton}
              onClick={handleSaveAndReturnHome}
              disabled={isSaving || isLoadingExpense}
            >
              {isSaving ? 'שומר...' : 'שמור וחזור לראשי'}
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Full Screen Image Modal */}
      {showImagePopup && imagePreview && (
        <div
          className={styles.imageFloatingPopup}
          style={{ left: popupPosition.x, top: popupPosition.y }}
          role="dialog"
          aria-label="תצוגת תמונת חשבונית"
        >
          <div
            className={styles.imagePopupHeader}
            onMouseDown={handlePopupHeaderMouseDown}
          >
            <button
              type="button"
              className={styles.imagePopupClose}
              onClick={closeImagePopup}
              aria-label="סגור"
            >
              ×
            </button>
          </div>
          <div
            className={styles.imagePopupBody}
            onWheel={handleWheel}
          >
            <img
              src={imagePreview}
              alt="Invoice preview"
              className={styles.imagePopupImage}
              style={{
                transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                cursor: isDraggingImage ? 'grabbing' : imageZoom > 1 ? 'grab' : 'zoom-in',
              }}
              onMouseDown={handleImageMouseDown}
              onMouseMove={handleImageMouseMove}
              onMouseUp={handleImageMouseUp}
              onMouseLeave={handleImageMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              draggable={false}
            />
            {imageZoom !== 1 && (
              <div className={styles.imagePopupZoomIndicator}>
                {imageZoom.toFixed(1)}x
              </div>
            )}
          </div>
        </div>
      )}
      {recurrenceDeleteOpen && (
        <RecurrenceDeleteDialog
          onChoose={handleRecurrenceDeleteChoice}
          onCancel={() => setRecurrenceDeleteOpen(false)}
        />
      )}
    </div>
  );
};

export default ExpenseEditorPage; 